import React from 'react'

import {Check} from '../forms/index.js'
import TableHeaderCell from './TableHeaderCell.js'
import TableSettings from './TableSettings.js'
import SelectionMenu from './SelectionMenu.js'
import {CellLayout, InternalColumn} from './types.js'
import {checkboxCellStyle, checkboxGapStyle, headerRowStyle} from './styles.js'
import t from '../../../helpers/Languages.js'

interface TableHeaderProps {
    cells: CellLayout[]
    columns: InternalColumn[]
    noPagination?: boolean
    onSort: (id: string) => void
    sortId?: string
    sortDecending: boolean
    canSelect: boolean
    allRowsSelected: boolean
    someRowsSelected: boolean
    onToggleAll: () => void
    onAddPage: () => void
    pageSize: number
    onChangePageSize: (pageSize: string) => void
    onToggleColumn: (id: string) => void
    options: Record<string, string>
    onSelectOption: (column: InternalColumn, value: string) => void
    hasSettings: boolean
    onReset: () => void
    onResizeStart: (id: string, event: React.MouseEvent) => void
    resizing: boolean
    /** Max height of the header menus: the table's height below the header */
    menuMaxHeight: number
}

const TableHeader = ({
    cells,
    columns,
    noPagination,
    onSort,
    sortId,
    sortDecending,
    canSelect,
    allRowsSelected,
    someRowsSelected,
    onToggleAll,
    onAddPage,
    pageSize,
    onChangePageSize,
    onToggleColumn,
    options,
    onSelectOption,
    hasSettings,
    onReset,
    onResizeStart,
    resizing,
    menuMaxHeight
}: TableHeaderProps) => {
    return (
        <div role='rowgroup' style={{flexShrink: 0}}>
            <div role='row' style={headerRowStyle}>
                {canSelect && (
                    <div role='columnheader' style={checkboxCellStyle}>
                        <Check
                            aria-label={t('tableSelectAll')}
                            checked={allRowsSelected}
                            indeterminate={someRowsSelected}
                            onChange={onToggleAll}
                        />

                        <div style={checkboxGapStyle} />

                        <SelectionMenu onAddPage={onAddPage} maxHeight={menuMaxHeight} />
                    </div>
                )}

                {cells.map((cell, index) => (
                    <TableHeaderCell
                        key={cell.id}
                        column={cell.column}
                        width={cell.width}
                        sorted={sortId === cell.id}
                        sortDecending={sortDecending}
                        onSort={onSort}
                        option={options[cell.id]}
                        onSelectOption={onSelectOption}
                        // Every column but the last: dragging moves its border with the next column
                        resizable={index < cells.length - 1}
                        resizing={resizing}
                        menuMaxHeight={menuMaxHeight}
                        onResizeStart={onResizeStart}
                        infoLeft={index > cells.length - 4}
                    />
                ))}

                <TableSettings
                    columns={columns}
                    maxHeight={menuMaxHeight}
                    noPagination={noPagination}
                    pageSize={pageSize}
                    onChangePageSize={(event) => onChangePageSize(event.target.value)}
                    onToggleColumn={onToggleColumn}
                    hasSettings={hasSettings}
                    onReset={onReset}
                />
            </div>
        </div>
    )
}

export default TableHeader
